import React, { useState, useEffect, useRef } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { useNavigate } from 'react-router-dom';
import SmsService from '../../../services/SmsService';

// 테스트 설정
const TEST_VERIFICATION_CODE = '923496';

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  organization: string;
  phoneNumber: string;
}

const SignUpForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    organization: '',
    phoneNumber: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false); // 회원가입 진행 중 상태

  // SMS 인증 관련 상태
  const [sendingSms, setSendingSms] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationSuccess, setVerificationSuccess] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0); // 재발송 방지 쿨다운
  const [verifyingCode, setVerifyingCode] = useState(false); // 인증번호 확인 중 상태

  // 각 입력 필드에 대한 ref 생성
  const nameRef = useRef<HTMLInputElement>(null);
  const organizationRef = useRef<HTMLInputElement>(null);
  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  
  // 타이머 관리를 위한 ref
  const verificationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 현재 인증 상태를 ref로 추적 (타이머에서 최신 상태 확인용)
  const phoneVerifiedRef = useRef(phoneVerified);
  
  // phoneVerified 상태 변경 시 ref도 업데이트
  useEffect(() => {
    phoneVerifiedRef.current = phoneVerified;
  }, [phoneVerified]);

  // 포커스 시 스크롤 조정 함수
  const handleFocus = (ref: React.RefObject<HTMLInputElement | null>) => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  // 전화번호 인증 생략 도메인 체크
  const isExemptDomain = (email: string): boolean => {
    const exemptDomains = ['bnfc.or.kr', 'korea.kr', 'busanpa.com'];
    const domain = email.split('@')[1];
    return exemptDomains.includes(domain);
  };

  // 인증 필요 여부 확인
  const needsPhoneVerification = (): boolean => {
    return !isExemptDomain(formData.email);
  };

  // 회원가입 버튼 활성화 조건
  const isFormValid = (): boolean => {
    const basicFieldsValid = formData.email.trim() && 
                           formData.password.trim() && 
                           formData.confirmPassword.trim() && 
                           formData.name.trim() && 
                           formData.organization.trim() && 
                           formData.phoneNumber.trim();
    
    if (!basicFieldsValid) return false;
    
    // 전화번호 인증이 필요한 경우 인증 완료 여부 확인
    if (needsPhoneVerification()) {
      return phoneVerified;
    }
    
    return true;
  };

  useEffect(() => {
    // 이미 로그인된 사용자 체크 (회원가입 진행 중이 아닐 때만)
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && !isSigningUp) {
        alert('이미 로그인되어 있습니다. 메인 페이지로 이동합니다.');
        navigate('/main');
      }
    });

    return () => {
      unsubscribe();
    };
  }, [navigate, isSigningUp]);

  // 이메일 변경 시 인증 상태 초기화
  useEffect(() => {
    clearTimers(); // 기존 타이머들 정리
    setPhoneVerified(false);
    setShowVerificationInput(false);
    setVerificationCode('');
    setVerificationError(null);
    setVerificationSuccess(null);
    setRemainingTime(0);
    setCooldownTime(0);
    
    // 특정 도메인의 경우 인증 생략
    if (formData.email && isExemptDomain(formData.email)) {
      setPhoneVerified(true);
      setVerificationSuccess('해당 도메인은 전화번호 인증이 생략됩니다.');
    }
  }, [formData.email]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // SMS 인증번호 발송
  const handleSendVerification = async () => {
    if (!formData.phoneNumber) {
      setVerificationError('전화번호를 입력해주세요.');
      return;
    }

    // 전화번호 유효성 검사
    const normalizedPhone = formData.phoneNumber.replace(/-/g, '');
    if (!/^01[0-9]{8,9}$/.test(normalizedPhone)) {
      setVerificationError('올바른 전화번호 형식이 아닙니다.');
      return;
    }

    setSendingSms(true);
    setVerificationError(null);
    setVerificationSuccess(null);

    try {
      const { requestId } = await SmsService.sendVerificationCodeForSignup(normalizedPhone);
      
      setShowVerificationInput(true);
      setVerificationSuccess('인증번호가 발송되었습니다. 3분 이내에 입력해주세요.');
      
      // 인증번호 입력 타이머 시작 (3분)
      setRemainingTime(180); // 3분 = 180초
      startTimer();
      
      // 재발송 방지 쿨다운 시작 (1분)
      setCooldownTime(60); // 1분 = 60초
      startCooldownTimer();
      
    } catch (error) {
      console.error('SMS 발송 실패:', error);
      setVerificationError(error instanceof Error ? error.message : 'SMS 발송에 실패했습니다.');
    } finally {
      setSendingSms(false);
    }
  };

  // 인증번호 검증
  const handleVerifyCode = async () => {
    if (!formData.phoneNumber || !verificationCode) {
      setVerificationError('인증번호를 입력해주세요.');
      return;
    }

    setVerifyingCode(true);
    setVerificationError(null);

    try {
      const normalizedPhone = formData.phoneNumber.replace(/-/g, '');
      const isValid = await SmsService.verifyCodeForSignup(normalizedPhone, verificationCode);

      if (isValid) {
        clearTimers(); // 모든 타이머 정리
        setVerificationSuccess('전화번호 인증이 완료되었습니다.');
        setPhoneVerified(true);
        setShowVerificationInput(false);
        setVerificationCode('');
        setRemainingTime(0);
        setCooldownTime(0); // 인증 성공 시 쿨다운 초기화
        setVerificationError(null);
      } else {
        setVerificationError('인증번호가 올바르지 않거나 만료되었습니다.');
      }
    } catch (error) {
      console.error('인증번호 확인 실패:', error);
      setVerificationError(error instanceof Error ? error.message : '인증번호 확인에 실패했습니다.');
    } finally {
      setVerifyingCode(false);
    }
  };

  // 기존 타이머들 정리
  const clearTimers = () => {
    if (verificationTimerRef.current) {
      clearInterval(verificationTimerRef.current);
      verificationTimerRef.current = null;
    }
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
  };

  // 인증번호 입력 타이머 시작 (3분)
  const startTimer = () => {
    // 기존 타이머가 있다면 정리
    if (verificationTimerRef.current) {
      clearInterval(verificationTimerRef.current);
    }
    
    verificationTimerRef.current = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          clearInterval(verificationTimerRef.current!);
          verificationTimerRef.current = null;
          
          // 인증이 아직 완료되지 않은 경우에만 만료 메시지 표시
          if (!phoneVerifiedRef.current) {
            setShowVerificationInput(false);
            setVerificationError('인증 시간이 만료되었습니다.');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 재발송 방지 쿨다운 타이머 시작 (1분)
  const startCooldownTimer = () => {
    // 기존 타이머가 있다면 정리
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    
    cooldownTimerRef.current = setInterval(() => {
      setCooldownTime(prev => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current!);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 시간 포맷 (mm:ss)
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setIsSigningUp(true); // 회원가입 시작
    setError('');

    try {
      // 전화번호에서 하이픈 제거
      const normalizedPhone = formData.phoneNumber.replace(/-/g, '');
      
      // 전화번호 유효성 검사 (하이픈 제거 후)
      if (!/^01[0-9]{8,9}$/.test(normalizedPhone)) {
        setError('올바른 전화번호 형식이 아닙니다.');
        setLoading(false);
        return;
      }

      // 이메일 유효성 검사
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('올바른 이메일 형식이 아닙니다.');
        setLoading(false);
        return;
      }

      // 비밀번호 유효성 검사
      if (formData.password.length < 6) {
        setError('비밀번호는 6자 이상이어야 합니다.');
        setLoading(false);
        return;
      }

      // 비밀번호 확인
      if (formData.password !== formData.confirmPassword) {
        setError('비밀번호가 일치하지 않습니다.');
        setLoading(false);
        return;
      }

      // 전화번호 인증 확인
      if (needsPhoneVerification() && !phoneVerified) {
        setError('전화번호 인증을 완료해주세요.');
        setLoading(false);
        return;
      }

      // Firebase Auth로 회원가입
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // 사용자 프로필 정보 저장
      await setDoc(doc(db, 'user_profiles', userCredential.user.uid), {
        email: formData.email,
        name: formData.name,
        organization: formData.organization,
        phoneNumber: normalizedPhone, // 하이픈이 제거된 전화번호 저장
        phoneVerified: phoneVerified, // 전화번호 인증 상태 저장
        userLevel: 10, // 이메일 미인증 사용자 레벨
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      // 이메일 인증 메일 발송
      await sendEmailVerification(userCredential.user);

      // 회원가입 완료 메시지 표시
      alert('회원가입을 축하합니다! 이메일 인증 페이지로 이동합니다.');

      // 이메일 인증 페이지로 이동
      navigate('/email-verification', {
        state: {
          email: formData.email,
          message: '회원가입이 완료되었습니다. 이메일 인증 후 로그인해주세요.'
        }
      });
    } catch (error: any) {
      console.error('회원가입 오류:', error);
      let errorMessage = '회원가입 중 오류가 발생했습니다.';
      
      // Firebase 오류 메시지 한글화
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = '이미 사용 중인 이메일입니다.';
          break;
        case 'auth/invalid-email':
          errorMessage = '유효하지 않은 이메일 주소입니다.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = '이메일/비밀번호 로그인이 비활성화되어 있습니다.';
          break;
        case 'auth/weak-password':
          errorMessage = '비밀번호가 너무 약합니다.';
          break;
      }
      
      setError(errorMessage);
      setIsSigningUp(false); // 회원가입 실패 시 상태 복원
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      <div className="space-y-6">
        {/* 이메일 입력 */}
        <div>
          <label htmlFor="email" className="block text-gray-800 text-base font-semibold font-['Pretendard'] leading-9">
            이메일
          </label>
          <div className="mt-2">
            <input
              ref={emailRef}
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              className="w-full h-14 px-4 py-3.5 bg-white rounded-sm outline outline-[1px] md:outline-[0.50px] outline-[#d6dae5] text-base font-medium font-['Pretendard'] leading-relaxed"
              placeholder="이메일 주소를 입력해주세요"
              value={formData.email}
              onChange={handleChange}
              onFocus={() => handleFocus(emailRef)}
              required
            />
            <p className="mt-2 text-sm text-gray-500 font-['Pretendard']">
              이메일 주소는 로그인시 아이디로 사용됩니다. 반드시 실제 이메일주소를 입력해주세요.
            </p>
          </div>
        </div>

        {/* 비밀번호 입력 */}
        <div>
          <label htmlFor="password" className="block text-gray-800 text-base font-semibold font-['Pretendard'] leading-9">
            비밀번호
          </label>
          <div className="mt-2">
            <input
              ref={passwordRef}
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              className="w-full h-14 px-4 py-3.5 bg-white rounded-sm outline outline-[1px] md:outline-[0.50px] outline-[#d6dae5] text-base font-medium font-['Pretendard'] leading-relaxed"
              placeholder="비밀번호를 입력해주세요"
              value={formData.password}
              onChange={handleChange}
              onFocus={() => handleFocus(passwordRef)}
              required
            />
          </div>
        </div>

        {/* 비밀번호 확인 입력 */}
        <div>
          <label htmlFor="confirmPassword" className="block text-gray-800 text-base font-semibold font-['Pretendard'] leading-9">
            비밀번호 확인
          </label>
          <div className="mt-2">
            <input
              ref={confirmPasswordRef}
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              className="w-full h-14 px-4 py-3.5 bg-white rounded-sm outline outline-[1px] md:outline-[0.50px] outline-[#d6dae5] text-base font-medium font-['Pretendard'] leading-relaxed"
              placeholder="비밀번호를 재입력해주세요"
              value={formData.confirmPassword}
              onChange={handleChange}
              onFocus={() => handleFocus(confirmPasswordRef)}
              required
            />
          </div>
        </div>

        {/* 이름 입력 */}
        <div>
          <label htmlFor="name" className="block text-gray-800 text-base font-semibold font-['Pretendard'] leading-9">
            이름
          </label>
          <div className="mt-2">
            <input
              ref={nameRef}
              id="name"
              name="name"
              type="text"
              className="w-full h-14 px-4 py-3.5 bg-white rounded-sm outline outline-[1px] md:outline-[0.50px] outline-[#d6dae5] text-base font-medium font-['Pretendard'] leading-relaxed"
              placeholder="이름을 입력해주세요"
              value={formData.name}
              onChange={handleChange}
              onFocus={() => handleFocus(nameRef)}
              required
            />
          </div>
        </div>

        {/* 소속 입력 */}
        <div>
          <label htmlFor="organization" className="block text-gray-800 text-base font-semibold font-['Pretendard'] leading-9">
            소속
          </label>
          <div className="mt-2">
            <input
              ref={organizationRef}
              id="organization"
              name="organization"
              type="text"
              className="w-full h-14 px-4 py-3.5 bg-white rounded-sm outline outline-[1px] md:outline-[0.50px] outline-[#d6dae5] text-base font-medium font-['Pretendard'] leading-relaxed"
              placeholder="소속을 입력해주세요"
              value={formData.organization}
              onChange={handleChange}
              onFocus={() => handleFocus(organizationRef)}
              required
            />
          </div>
        </div>

        {/* 전화번호 입력 */}
        <div>
          <label htmlFor="phoneNumber" className="block text-gray-800 text-base font-semibold font-['Pretendard'] leading-9">
            전화번호
          </label>
          <div className="mt-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                ref={phoneNumberRef}
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                className="flex-1 min-w-0 h-14 px-4 py-3.5 bg-white rounded-sm outline outline-[1px] md:outline-[0.50px] outline-[#d6dae5] text-base font-medium font-['Pretendard'] leading-relaxed"
                placeholder="전화번호를 입력해주세요"
                value={formData.phoneNumber}
                onChange={handleChange}
                onFocus={() => handleFocus(phoneNumberRef)}
                required
              />
              {needsPhoneVerification() && !phoneVerified && (
                <button
                  type="button"
                  onClick={handleSendVerification}
                  disabled={sendingSms || !formData.phoneNumber || cooldownTime > 0}
                  className="w-full sm:w-auto sm:flex-shrink-0 px-4 py-2 bg-blue-600 text-white text-sm font-medium font-['Pretendard'] rounded-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {sendingSms ? '발송 중...' : cooldownTime > 0 ? `재발송 (${cooldownTime}초)` : '인증'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 인증번호 입력 필드 */}
        {showVerificationInput && (
          <div>
            <label htmlFor="verificationCode" className="block text-gray-800 text-base font-semibold font-['Pretendard'] leading-9">
              인증번호
            </label>
            <div className="mt-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="인증번호 6자리를 입력하세요"
                  className="flex-1 min-w-0 h-14 px-4 py-3.5 bg-white rounded-sm outline outline-[1px] md:outline-[0.50px] outline-[#d6dae5] text-base font-medium font-['Pretendard'] leading-relaxed"
                  maxLength={6}
                />
                <button
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={verifyingCode || !verificationCode || verificationCode.length !== 6}
                  className="w-full sm:w-auto sm:flex-shrink-0 px-4 py-2 bg-green-600 text-white text-sm font-medium font-['Pretendard'] rounded-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {verifyingCode ? '확인 중...' : '확인'}
                </button>
              </div>
              {remainingTime > 0 && (
                <p className="mt-2 text-sm text-gray-500 font-['Pretendard']">
                  남은 시간: {formatTime(remainingTime)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* SMS 인증 관련 메시지 */}
        {verificationError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded font-['Pretendard']">
            {verificationError}
          </div>
        )}
        
        {verificationSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded font-['Pretendard']">
            {verificationSuccess}
          </div>
        )}

        {error && (
          <div className="text-red-600 text-sm font-medium font-['Pretendard']">
            {error}
          </div>
        )}

        <div className="mt-16">
          <button
            type="submit"
            className={`w-full h-16 rounded-sm ${
              isFormValid()
                ? 'bg-blue-700' 
                : 'bg-gray-400'
            } disabled:bg-gray-400`}
            disabled={loading || !isFormValid()}
          >
            <span className="text-center justify-center text-white text-lg font-bold font-['Pretendard'] leading-9">
              {loading ? '처리중...' : '회원가입'}
            </span>
          </button>
        </div>
      </div>
    </form>
  );
};

export default SignUpForm; 